import { Pause, Play, Song } from '@tunasong/icons'
import { dayjs } from '@tunasong/models'
import type { Entity, Media, Persisted } from '@tunasong/schemas'
import {
  EntityIcon,
  PulseProgress,
  useNavigateToEntity,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  type ListItemButtonProps,
  Typography,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, MouseEvent } from 'react'
import { useCallback } from 'react'
import { useLinkedSong } from './linked-song.js'
import styles from './playlist-list-item.module.css'

export interface PlayListItemProps extends ListItemButtonProps {
  ref?: React.Ref<HTMLDivElement>
  entity: Persisted<Media>
  onPlayClicked?: (ev: MouseEvent) => void
  playing?: boolean
  progressLabel?: string
  showUpdated?: boolean
  showPageButton?: boolean
  showPlayButton?: boolean
}

export const PlayListItem: FC<PlayListItemProps> = props => {
  const {
    entity,
    progressLabel,
    playing = false,
    onPlayClicked,
    showPlayButton = false,
    showPageButton = true,
    showUpdated = false,
    className,
    ...restProps
  } = props

  // Shared definition with the performance stage — the melody icon shows only
  // when a real song/page document is linked (a bare folder parent does not count).
  const { song } = useLinkedSong(entity)

  const navigateToEntity = useNavigateToEntity()
  const handleNavigate = useCallback(
    (entityOrEntityId?: Persisted<Entity> | string) => (ev: MouseEvent) => {
      const entityId = typeof entityOrEntityId === 'string' ? entityOrEntityId : entityOrEntityId?.id
      if (entityId) {
        ev.preventDefault()
        ev.stopPropagation()
        const navigationMode = ev.shiftKey ? 'dialog' : 'page'
        navigateToEntity({ id: entityId }, { navigationMode })
      }
    },
    [navigateToEntity]
  )

  const secondary = showUpdated ? `Updated ${dayjs(entity.updatedAt).fromNow()}` : undefined

  /** The actions sit beside the row's button, never inside it (axe nested-interactive) */
  const actions =
    showPlayButton || (showPageButton && song) ? (
      <>
        {showPlayButton && (
          <IconButton
            onClick={onPlayClicked}
            title="Toggle play"
            aria-label={`${playing ? 'Pause' : 'Play'} ${entity.name ?? 'clip'}`}
          >
            {playing ? (
              <>
                <Typography variant="caption">{progressLabel}</Typography>
                <PulseProgress size="small" />
                <Pause />
              </>
            ) : (
              <Play />
            )}
          </IconButton>
        )}

        {showPageButton && song ? (
          <IconButton onClick={handleNavigate(song.id)} title="Go to song/page">
            <Song />
          </IconButton>
        ) : null}
      </>
    ) : null

  return (
    <ListItem disablePadding secondaryAction={actions}>
      <ListItemButton className={cn(styles.listItem, className)} {...restProps}>
        <ListItemIcon>
          <EntityIcon entity={entity} size={'medium'} />
        </ListItemIcon>
        <ListItemText primary={entity.name} secondary={secondary} />
      </ListItemButton>
    </ListItem>
  )
}
